import { useState } from 'react';
import { Link } from 'react-router-dom';
import { faqs } from '@/mocks/faqs';
import { siteInfo } from '@/mocks/site';
import usePageMeta from '@/hooks/usePageMeta';
import JsonLd from '@/components/feature/JsonLd';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumbs from '@/components/feature/Breadcrumbs';

const FaqPage = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  usePageMeta({
    title: '常见问题 - 内容来源与热度值说明 - 爆瓜爆料',
    description:
      '爆瓜爆料常见问题解答：内容来源如何核实、吃瓜热度值怎么算、第一现场复盘与普通快讯的区别、如何投稿以及多久回复，一次讲清楚。',
    keywords: '爆瓜爆料常见问题,热度值算法,爆料投稿,内容核实',
    canonical: 'https://sibzb.cn/faq',
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: { '@type': 'Answer', text: faq.answer },
          })),
        }}
      />
      <SiteHeader />

      <main className="flex-1 px-4 md:px-6 py-6 md:py-8">
        <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '常见问题' }]} />

        <header className="mt-5 max-w-3xl">
          <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground-950">常见问题</h1>
          <p className="mt-3 text-sm md:text-base leading-7 text-foreground-600">
            关于爆瓜爆料的内容来源、吃瓜热度值算法以及投稿方式，我们把最常被问到的几个问题整理在这里。
            如果这里没有你要的答案，可以通过意见反馈随时找到我们。
          </p>
        </header>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 flex flex-col gap-3" aria-label="问题列表">
            {faqs.map((faq, index) => {
              const isOpen = openIndex === index;
              return (
                <div
                  key={faq.question}
                  className={`rounded-lg border transition-colors ${
                    isOpen ? 'border-primary-300 bg-primary-50' : 'border-background-200 bg-background-50'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    aria-expanded={isOpen}
                    className="w-full flex items-center justify-between gap-4 px-4 md:px-5 py-4 text-left cursor-pointer"
                  >
                    <h2 className="font-heading text-base md:text-lg font-bold text-foreground-950">
                      {faq.question}
                    </h2>
                    <i
                      className={`${isOpen ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-primary-700 shrink-0`}
                    />
                  </button>
                  {isOpen && (
                    <p className="px-4 md:px-5 pb-5 text-sm leading-7 text-foreground-700">{faq.answer}</p>
                  )}
                </div>
              );
            })}
          </section>

          <aside className="lg:col-span-1 flex flex-col gap-4">
            <div className="rounded-lg bg-background-100 border border-background-200 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-lightbulb-line text-secondary-600" />
                三个关键词看懂我们
              </h2>
              <ul className="mt-4 space-y-3 text-sm text-foreground-700">
                {siteInfo.sloganList.map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <i className="ri-checkbox-circle-line text-primary-600" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg bg-primary-800 text-background-50 p-5">
              <h2 className="font-heading text-base font-bold">还有疑问？</h2>
              <p className="mt-2 text-sm leading-6 text-primary-100">
                欢迎直接给我们留言，编辑团队会在工作日尽快回复。
              </p>
              <Link
                to="/contact"
                className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-400 px-4 py-2 text-sm text-secondary-950 transition-colors hover:bg-secondary-300"
              >
                <i className="ri-mail-send-line" />
                联系我们
              </Link>
            </div>

            <div className="rounded-lg border border-background-200 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">热门栏目</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to="/category/yingshi"
                  className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-800"
                >
                  影视爆料
                </Link>
                <Link
                  to="/category/zongyi"
                  className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-800"
                >
                  综艺现场
                </Link>
                <Link
                  to="/category/mingxing"
                  className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-800"
                >
                  明星日常
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default FaqPage;